import React from 'react';
import PropTypes from 'prop-types';

import Ad from '../Ad/Ad';
import { AdSizes } from '../Ad/AdSizes';

import FeaturedShow, { FeaturedShowPropTypes } from './FeaturedShow';

/**
 * @summary Interleave `Ad` React components into the grid of featured shows.
 * @param {Array<React.ReactNode>} elements
 * @param {string} pageId
 * @returns {Array<React.ReactNode>}
 */
const interleaveAds = (elements, pageId) => {
	const newElements = elements.slice();
	let adCount = 1;
	let pos = 3;
	const interval = 4;
	const totalElements = elements.length;

	while (pos <= totalElements) {
		const sizes = {
			'xs sm md lg': AdSizes.BIG_BOX,
			'xl xxl': (adCount < 3) ? AdSizes.LEADERBOARD : AdSizes.HIDDEN,
		};

		const adElement = (
			<Ad
				key={`${pageId}-ad-${adCount}`}
				sizes={sizes}
				htmlId={`${pageId}-ad-${adCount}`}
				wrapperClass={`${pageId}-ad-${adCount}`}
				className={`${pageId}-ad-${adCount}-wrapper`}
				pos={`${adCount}`}
			/>
		);

		newElements.splice(pos, 0, adElement); // insert ad in every 4th position
		adCount += 1;
		pos += interval;
	}

	return newElements;
};

/**
 * @summary Get the list of featured shows as an array of JSX elements.
 * If enabling ads, `interleaveAds` then does just that on the list this function returns.
 *
 * @param {Array<(import('./FeaturedShowsTypes').Show)>} shows
 * @returns {Array<JSX.Element>}
 */
export function getFeaturedShows(shows) {
	return (shows.map((show) => (
		<React.Fragment key={show?.id ?? ''}>
			<FeaturedShow show={show} />
		</React.Fragment>
	)));
}

/**
 * @summary Shows optionally interleaved with `Ad` React components.
 * @param {object} props
 * @param {Array<import('../../components/FeaturedShows/FeaturedShowsTypes').Show>} props.shows
 * @param {string} props.pageId Page ID for interleaved ads. Not needed if `withAds` is not included.
 * @param {boolean} props.withAds Should ads be enabled?
 * @returns {JSX.Element}
 */
const FeaturedShows = ({ shows = [], pageId = '', withAds = false }) => {
	const featuredShowElements = getFeaturedShows(shows);

	return (
		<div className="shows__grid">
			{withAds ? interleaveAds(featuredShowElements, pageId) : featuredShowElements}
		</div>
	);
};

FeaturedShows.propTypes = {
	shows: PropTypes.arrayOf(PropTypes.shape(FeaturedShowPropTypes)),
	pageId: PropTypes.string,
	withAds: PropTypes.bool,
};

export default FeaturedShows;
